import { cacheKeys } from "@constants/cache-keys";
import { hubAxios, isAxiosError } from "@helpers/lib/axios";
import { HUB_ENDPOINTS } from "@memorang/constants/urls";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Platform } from "react-native";

type UseCacheOptions<T> = {
	key: string;
	defaultData: T;
	enabled?: boolean;
	staleTime?: number;
	shouldInvalidate?: boolean;
	shouldOptimisticUpdate?: boolean;
};

const DEFAULT_STALE_TIME = 1000 * 60 * 60 * 24;
const useCache = <T>({
	key,
	defaultData,
	enabled = true,
	staleTime = DEFAULT_STALE_TIME,
	shouldInvalidate = true,
	shouldOptimisticUpdate = false,
}: UseCacheOptions<T>) => {
	const queryClient = useQueryClient();

	const {
		data: cacheData,
		isLoading,
		error,
		isRefetching,
	} = useQuery({
		queryKey: [cacheKeys.cache, key],
		queryFn: async (): Promise<T> => {
			if (!key) {
				return defaultData;
			}

			try {
				const response = await hubAxios.get(HUB_ENDPOINTS.CACHE, {
					params: { key },
				});
				return response.data.data || defaultData;
			} catch (error) {
				if (isAxiosError(error) && error.response?.status === 404) {
					return defaultData;
				}
				throw new Error("Failed to fetch cache data");
			}
		},
		enabled: !!key && enabled,
		staleTime,
	});

	const setCacheMutation = useMutation({
		mutationFn: async (data: Partial<T>) => {
			if (!key) {
				console.error("Cache key not available");
				return defaultData;
			}

			const cachedData = queryClient.getQueryData<T>([cacheKeys.cache, key]);
			const currentData = cachedData || defaultData;
			const updatedData = { ...currentData, ...data, platform: Platform.OS };

			// TODO(backend): scope cache key to verified caller userId; reject mismatched keys.
			await hubAxios.post(HUB_ENDPOINTS.CACHE, {
				key,
				data: updatedData,
			});

			return updatedData;
		},

		onMutate: async (newData: Partial<T>) => {
			if (!shouldOptimisticUpdate) {
				return;
			}

			await queryClient.cancelQueries({ queryKey: [cacheKeys.cache, key] });

			const previousData = queryClient.getQueryData<T>([cacheKeys.cache, key]);

			const currentData = previousData || defaultData;
			const optimisticData = { ...currentData, ...newData };
			queryClient.setQueryData([cacheKeys.cache, key], optimisticData);

			return { previousData };
		},
		// onError: (_err, _newData, context) => {
		// 	if (context?.previousData) {
		// 		queryClient.setQueryData([cacheKeys.cache, key], context.previousData);
		// 	}
		// },
		onSettled: () => {
			if (shouldInvalidate) {
				queryClient.invalidateQueries({ queryKey: [cacheKeys.cache, key] });
			}
		},
	});

	const deleteCacheMutation = useMutation({
		mutationFn: async () => {
			if (!key) {
				throw new Error("Cache key not available");
			}

			// TODO(backend): scope cache key to verified caller userId; reject mismatched keys.
			await hubAxios.delete(HUB_ENDPOINTS.CACHE, {
				data: { key },
			});
		},
		onSuccess: () => {
			queryClient.removeQueries({
				queryKey: [cacheKeys.cache, key],
			});
		},
	});

	return {
		data: cacheData,
		isLoading,
		error,
		setCache: setCacheMutation.mutateAsync,
		deleteCache: deleteCacheMutation.mutateAsync,
		isUpdating: setCacheMutation.isPending,
		isDeleting: deleteCacheMutation.isPending,
		isRefetching,
	};
};

export default useCache;
